import { EventTypes, ROOT_NODE } from '@candulabs/craft';
import { ModeProvider } from 'canoe';
import { FormikHelpers, FormikProvider, useFormik } from 'formik';
import { ENDPOINTS, useActions } from 'hammock';
import { nanoid } from 'nanoid';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ContentMetadata, ContentType } from '@candu-ai/content/types';
import { validationSchemas } from '@candu-ai/placement/content-placements';
import { ContentPlacement } from '@candu-ai/placement/types';
import { Snapshot } from '@candu-ai/snapshots/types';
import { useToggle } from 'commons/hooks';
import { BuiltInComponents, CRAFT_ROOT_ID, isTooltipContent, isTooltipDocument } from 'craft/constants';
import { useEditor, useMultiNodes } from 'craft/hooks';
import { setToolboxTab } from 'craft/state';
import { ToolboxTabs } from 'craft/types';

import { InlinePlacement } from './InlinePlacements';
import { ModalPlacements } from './ModalPlacements';
import { TooltipPlacements } from './TooltipPlacements';
import { EditPlacementModal } from './components/modals';
import { getDefaultPlacement, saveNewPlacement, updateExistingPlacement } from './utils';

interface PlacementsFormProps {
  snapshotURL: string;
  contentMetadata: ContentMetadata;
  snapshot?: Snapshot;
  activePlacement?: ContentPlacement;
  onGoBack?: VoidFunction;
}

export const PlacementsForm = ({
  snapshotURL,
  contentMetadata,
  activePlacement,
  onGoBack,
}: PlacementsFormProps) => {
  const [editPlacementModalOpen, toggleEditPlacementModalOpen] = useToggle();
  const dispatch = useDispatch();
  const { actions } = useEditor();

  const { type: rootNodeType } = useMultiNodes([CRAFT_ROOT_ID]);
  const { update: updatePlacement, create: createPlacement } = useActions(
    ENDPOINTS.contentPlacements,
  );

  const [placementSaved, setPlacementSaved] = useState<{
    values: ContentPlacement;
    id?: string;
  } | null>(null);

  const defaultPlacement = useMemo(() => getDefaultPlacement(contentMetadata, snapshotURL), [
    contentMetadata,
    snapshotURL,
  ]);

  const [placement, setPlacement] = useState(activePlacement ?? defaultPlacement);
  const placementExists = placement.id !== '';

  const onPlacementSubmit = async (
    values: ContentPlacement,
    { resetForm }: FormikHelpers<ContentPlacement>,
  ) => {
    const editedPlacement = placementExists ? { ...values } : { ...values, id: nanoid(6) };

    if (placementExists) {
      await updateExistingPlacement({
        updatePlacement,
        contentId: contentMetadata.id,
        updatedPlacement: editedPlacement,
      });
    } else {
      await saveNewPlacement({
        createPlacement,
        contentId: contentMetadata.id,
        newPlacement: editedPlacement,
      });
    }

    resetForm({ values: editedPlacement });
    setPlacementSaved({ values: editedPlacement });

    // make sure to update the original placement passed to the forms when changes are saved
    setPlacement(editedPlacement);

    if (!placementExists && !isTooltipContent(contentMetadata.contentType)) {
      // when first creating a placement, switch to Component tab on save and select root node
      dispatch(setToolboxTab(ToolboxTabs.Component));
      actions.setEventNodes(EventTypes.selected, ROOT_NODE);
      // if it's an inline placement, go back to showing placements list
      onGoBack?.();
    }
  };

  const formikPlacement = useFormik<ContentPlacement>({
    initialValues: placement,
    validationSchema: validationSchemas[placement.type || ContentType.Inline],
    onSubmit: onPlacementSubmit,
  });

  return (
    <FormikProvider value={formikPlacement}>
      {rootNodeType === BuiltInComponents.Modal && (
        <ModalPlacements
          placement={placement}
          placementExists={placementExists}
          onEditUrl={toggleEditPlacementModalOpen}
          placementSaved={placementSaved}
          resetPlacementSaved={() => setPlacementSaved(null)}
        />
      )}

      {isTooltipDocument(rootNodeType) && (
        <TooltipPlacements
          placement={placement}
          onEditUrl={toggleEditPlacementModalOpen}
          placementSaved={placementSaved}
          resetPlacementSaved={() => setPlacementSaved(null)}
        />
      )}

      {rootNodeType === BuiltInComponents.Document && (
        <InlinePlacement
          placement={placement}
          onEditUrl={toggleEditPlacementModalOpen}
          placementSaved={placementSaved}
          resetPlacementSaved={() => setPlacementSaved(null)}
          onGoBack={onGoBack}
        />
      )}

      {editPlacementModalOpen && (
        <ModeProvider value={{ mode: 'Light' }}>
          <EditPlacementModal
            onClose={toggleEditPlacementModalOpen}
            onSubmit={onPlacementSubmit}
            placementExists={placementExists}
          />
        </ModeProvider>
      )}
    </FormikProvider>
  );
};
